/**
 * Anasayfa panoları (068) — backend DTO'larıyla **birebir** (`any` yasak, anayasa İlke IV).
 * Karar/varsayım gerekçeleri: `specs/068-anasayfa-iki-dashboard/spec.md`.
 */

/** Hangi pano görünüyor? Kullanıcının **kapsam yetkisinden** belirlenir — bkz. {@link viewFor}. */
export type DashboardView = 'manager' | 'my'

/**
 * Kullanıcının anasayfada göreceği **tek** pano (kullanıcı isteği 2026-08-21): Genel Müdür · Özel Kalem
 * Müdürü · Özel Kalem Personeli **Yönetici Raporu**, diğer tüm kullanıcılar **Raporum**.
 *
 * <p><b>Panolar arası geçiş YOKTUR</b> (aynı istek): kullanıcı yalnız kendi panosunu görür, "…Geçiş Yap"
 * butonu kaldırıldı ve `?view=` parametresi artık okunmuyor. 068 §K14'ün "aktif pano URL'de korunur"
 * kararı bu yüzden geçersizdir — korunacak bir seçim kalmadı.
 *
 * <p>Karar **pano yetkisinden** verilir: `DASHBOARD_GM_SCOPE` **veya** `DASHBOARD_YKB_SCOPE`
 * (Yönetim Kurulu Başkanı, 2026-10-12 — `DASHBOARD_GM_SCOPE`'un ikizi, aynı panoyu açar). Rol adı da,
 * kapsamın `kind`'ı da kullanılmaz. **`kind`'a bakmak bir hataydı** (2026-08-23): o alan kapsamın
 * *sonucudur* ve fail-soft'tur — HR'da Genel Müdür tanımı çözülemezse `DIRECT_REPORTS`/`SELF`'e iner.
 * Pano seçimi ona bağlandığında, yetkisi olan bir Genel Müdür hiçbir uyarı görmeden Raporum'a
 * düşüyordu (THY test bildirimi; arıza yalnız sunucu log'unda kalıyordu).
 *
 * <p><b>Neden kapsam yanıtından değil, `/me`'nin yetkisinden (2026-10-13):</b> sunucunun
 * `scope.managerReport` alanı bu iki yetkinin <b>kendisidir</b> (`DashboardScopeService`:
 * `gmScope || ykbScope`) ve aynı küme `/me` ile istemcide zaten durur — vekil modunda da aynı
 * politikayla (`ProxyGrantPolicy` iki kodu da düşürmez). **İki kod da birlikte okunmalıdır:** yalnız
 * birine bakmak, öteki yetkiyle gelen kullanıcıyı sessizce Raporum'a düşürür (merge'de yakalandı —
 * 122 YKB kodunu sunucuya bu değişiklikle aynı anda ekledi). Karar kapsam yanıtına bağlıyken anasayfa
 * önce kapsamı bekliyor, pano verisini ancak sonra istiyordu: bir sunucu turu daha ("Pano yükleniyor…").
 * Ölçüldü (lokal → RDC dev DB): persona geçişi 1756 → 1391 ms. Yetki mantığı istemciye TAŞINMAZ —
 * menü ve butonlar gibi, sunucunun verdiği yetki kodu okunur.
 */
export function viewFor(canSeeManagerReport: boolean): DashboardView {
  return canSeeManagerReport ? 'manager' : 'my'
}

/** Dönem seçenekleri — tek kaynak; i18n anahtarı `home.period.<değer>` (analiz §1.4.1.2). */
export const DASHBOARD_PERIODS = [
  'LAST_1_WEEK',
  'LAST_1_MONTH',
  'LAST_3_MONTHS',
  'LAST_6_MONTHS',
  'LAST_1_YEAR',
  'CUSTOM',
] as const

export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number]

/** Varsayılan dönem (analiz §1.4.1.2 "ilk açılışta Son 1 Yıl") — backend varsayılanıyla aynı. */
export const DEFAULT_PERIOD: DashboardPeriod = 'LAST_1_YEAR'

/**
 * **Raporum'un** dönem seçenekleri (Raporum revizyonu, 2026-08-19): dört sabit aralık. Yönetici
 * Raporu'nun listesinden iki eksiği vardır — **Son 6 Ay** ve **Özel Tarih Aralığı** — çünkü analiz
 * seçenekleri tek tek sayıyor ve mockup'ta yalnız bu dört değer var.
 *
 * <p>Özel aralık olmadığı için Raporum'da takvim alanları hiç çizilmez ve "başlangıç/bitiş" doğrulaması
 * devreye girmez; `CUSTOM`'a düşme yolu da yoktur.
 */
export const MY_REPORT_PERIODS: readonly DashboardPeriod[] = [
  'LAST_1_WEEK',
  'LAST_1_MONTH',
  'LAST_3_MONTHS',
  'LAST_1_YEAR',
]

/**
 * Raporum'un varsayılan dönemi (analiz: "Varsayılan değer: Son 1 Ay").
 *
 * <p>Yönetici Raporu'nun varsayılanından (`LAST_1_YEAR`) **bilinçli olarak farklıdır**; bu yüzden iki pano
 * artık dönem durumunu **paylaşmaz** (068'in "geçişte seçili dönem korunur" kararı 2026-08-19'da tersine
 * döndü). Paylaşılan tek bir durumla Raporum'a her geçişte Yönetici Raporu'nun dönemi taşınır ve
 * kullanıcı analizin istediği varsayılanı hiç görmezdi.
 */
export const DEFAULT_MY_PERIOD: DashboardPeriod = 'LAST_1_MONTH'

/** Sunucunun çözdüğü tarih penceresi (yankı); `start`/`end` "YYYY-MM-DD". */
export interface DashboardRange {
  period: DashboardPeriod
  start: string
  end: string
}

/** Kapsamın türü — kişi filtresinin başlığını belirler (sunucu çözer, istemci yeniden hesaplamaz). */
export type ScopeKind = 'GENERAL_MANAGER' | 'DIRECT_REPORTS' | 'SELF'

export interface DashboardScope {
  kind: ScopeKind
  /** Kişi filtresi ekranda gösterilsin mi (analiz §1.12.1 — küme yoksa alan hiç gelmez). */
  filterVisible: boolean
  /**
   * Kullanıcı **Yönetici Raporu'nu** mu görür (`DASHBOARD_GM_SCOPE` ya da `DASHBOARD_YKB_SCOPE`
   * yetkisi). `kind`'tan **türetilmez** — pano yetkidir, kişi kümesi (`kind`) ayrı karardır. İstemci
   * pano seçimini bu alandan değil `/me`'deki aynı yetkilerden yapar (bkz. {@link viewFor}); alan
   * sözleşmede kalır.
   */
  managerReport: boolean
}

/**
 * Yönetici Raporu'nun kova anahtarları (086 — yeni statü kataloğu) — sunucunun `bucket` değerleriyle
 * birebir. Sıra **sunucudan** gelir (`MANAGER_DISTRIBUTION_SLICES`); bu tip yalnız tür güvenliği içindir,
 * ikinci bir sıralama/etiket kaynağı DEĞİLDİR (adlar da sunucudan gelir — §K8).
 *
 * `CANCELLED`, 085 ile kullanımdan kalkan İptal statüsünün eski kayıtlarıdır: yalnız Raporum'da dilim
 * üretir, Yönetici Raporu onu göstermez.
 */
export const ACTION_BUCKETS = [
  'PENDING',
  'PENDING_LATE',
  'COMPLETED_ON_TIME',
  'COMPLETED_LATE',
  'CLOSED_ON_TIME',
  'CLOSED_LATE',
  'CANCELLED',
] as const

export type ActionBucket = (typeof ACTION_BUCKETS)[number]

/**
 * Donut'un **üç ana durumu** (086): Aksiyon Bekleniyor / Tamamlandı / Kapatıldı. Her grup
 * {@link ACTION_BUCKETS}'ten iki kovanın (zamanında + gecikmiş) toplamıdır; kırılım sütun grafiğinde
 * gösterilir. Sıra ve adlar yine **sunucudan** gelir — bu sabit yalnız tür güvenliği ve seçim çözümü
 * içindir.
 */
export const ACTION_BUCKET_GROUPS = ['GROUP_PENDING', 'GROUP_COMPLETED', 'GROUP_CLOSED'] as const

export type ActionBucketGroup = (typeof ACTION_BUCKET_GROUPS)[number]

/**
 * Seçimin **tek** anahtarı: donut'ta bir **ana grup**, sütun grafiğinde tek bir **kova**. `?bucket=`
 * parametresi ikisini de taşır ve sunucu ikisini de çözer.
 */
export type ActionSelection = ActionBucket | ActionBucketGroup

/**
 * Alt tablonun seçimi — **çoklu** (kullanıcı isteği 2026-09-16). Boş dizi → seçim yok (tüm statüler).
 *
 * <p>Seçim bir anahtar **listesidir**, kova listesi değil: ana durumun kovalarına açılması (ve tersi)
 * `slice.group` üzerinden **sunucu verisiyle** yapılır (§K8 — istemci grup ⇄ kova sözlüğü tutmaz), o veri
 * ise ancak pano yüklendikten sonra vardır. Anahtar listesi URL'den veri beklemeden okunabilir; genişletme
 * gereken yerlerde {@link expandSelection} çağrılır.
 *
 * <p>Değerler **birleşir** (VEYA): tabloya, seçilen kovalardan en az birinde olan kayıtlar girer. Kesişim
 * tanımsız olurdu — bir aksiyon aynı anda tek bir kovadadır.
 */
export type ActionSelectionKeys = readonly ActionSelection[]

/**
 * Seçimin **yazım** sırası — URL ve önbellek anahtarı, aynı kümeye hangi sırayla tıklandığından bağımsız
 * olarak aynı çıksın diye (`?bucket=PENDING&bucket=CLOSED_LATE` ile tersi **aynı** sorgudur; ayrışsalardı
 * TanStack Query aynı veriyi iki kez çekerdi).
 *
 * <p>Ekrandaki **gösterim** sırası bu DEĞİLDİR: grafikler ve tablo açıklaması sunucudan gelen dilim
 * sırasını izler (§K8) — bu sabit yalnız iki teknik dizgeyi (URL, önbellek anahtarı) kararlı kılar.
 */
const SELECTION_ORDER: readonly string[] = [...ACTION_BUCKET_GROUPS, ...ACTION_BUCKETS]

/**
 * Panonun **açılışta** seçili gelen ana durumu (kullanıcı isteği 2026-09-11): "Görev Durum Dağılımı"
 * donut'unda **Aksiyon Bekleniyor** dilimi seçili gelir ve alt tablo o kümeyle açılır.
 *
 * <p>Seçimsiz hâlin **yerini almaz**: kullanıcı seçili dilime tıklayıp seçimi kaldırabilir ve o hâlde
 * tablo tüm statüleri gösterir (aşağıda, {@link parseSelection}). Bu sabit yalnız **açılışı** söyler.
 */
export const DEFAULT_OPEN_SELECTION: ActionSelectionKeys = ['GROUP_PENDING']

/**
 * URL'den gelen seçim (`?bucket=`, **tekrarlanabilir**); tanınmayan değerler **tek tek** düşer, hiçbiri
 * tanınmazsa sonuç boş dizidir. Tekrarlar ayıklanır ve liste {@link SELECTION_ORDER}'a göre sıralanır.
 *
 * <p><b>Seçimsiz hâl = TÜM statüler</b> (kullanıcı kararı 2026-09-11): satırlar ana duruma göre öbeklenir
 * (Aksiyon Bekleniyor → Tamamlandı → Kapatıldı) ve tablonun "Durum" kolonu ancak burada birden çok değer
 * yazar. Kümeyi **sunucu** belirler; istemci eskiden bir varsayılan kova sabiti
 * (`DEFAULT_ACTION_BUCKET = 'PENDING_LATE'`, 077 §K3) tutup seçimsiz hâli o kovaymış gibi işaretliyordu —
 * sabit kalktı: pano artık seçili açıldığı için seçimsiz durum açılışın değil, kullanıcının seçimi
 * **kaldırmasının** sonucudur ve orada beklenen daha dar bir kova değil, kümenin tamamıdır.
 */
export function parseSelection(raw: readonly string[]): ActionSelection[] {
  const unique = [...new Set(raw.filter((v) => SELECTION_ORDER.includes(v)))] as ActionSelection[]
  return unique.sort((a, b) => SELECTION_ORDER.indexOf(a) - SELECTION_ORDER.indexOf(b))
}

/**
 * Seçimin kapsadığı **kovalar**. Bir anahtar ana durumsa kovaları `slice.group`'tan, yani **sunucudan**
 * okunur — istemci ikinci bir grup → kova sözlüğü tutmaz (§K8, `statusName` ile aynı desen).
 *
 * <p>`slices` **alt kırılım** dilimleridir (`statusDistribution`): grup dilimlerinde `group === bucket`
 * olduğu için grup listesi verilseydi ana durum kendi kendine açılır, kovaları hiç görünmezdi.
 *
 * <p>Veri henüz gelmemişse boş küme döner. Sorun değildir: dağılım boşken kart zaten boş-durum çizer, yani
 * tıklanacak bir grafik yoktur.
 */
export function expandSelection(
  selection: readonly string[],
  slices: readonly StatusSlice[],
): Set<string> {
  const buckets = new Set<string>()
  for (const slice of slices) {
    if (selection.some((key) => slice.bucket === key || slice.group === key)) {
      buckets.add(slice.bucket)
    }
  }
  return buckets
}

/**
 * Kova kümesini **anahtarlara** büzer: kovalarının tamamı seçili olan ana durum kendi anahtarıyla
 * (`GROUP_*`) yazılır, kalan kovalar tek tek. Grup üyeliği yine **sunucudan** gelir.
 *
 * <p>Büzme iki şey kazandırır: URL kısa ve **eski bağlantılarla aynı** kalır (`?bucket=GROUP_PENDING`), ve
 * tablo açıklaması iki kova adı yerine tek bir ana durum adı yazar — "Aksiyon Bekleniyor", "Aksiyon
 * Bekleniyor (Zamanında), Aksiyon Bekleniyor (Gecikmiş)" değil.
 */
export function collapseSelection(
  buckets: ReadonlySet<string>,
  slices: readonly StatusSlice[],
): ActionSelection[] {
  const members = new Map<string, string[]>()
  for (const slice of slices) {
    if (slice.group == null) continue
    members.set(slice.group, [...(members.get(slice.group) ?? []), slice.bucket])
  }
  const keys: string[] = []
  const covered = new Set<string>()
  for (const [group, groupBuckets] of members) {
    if (groupBuckets.every((b) => buckets.has(b))) {
      keys.push(group)
      groupBuckets.forEach((b) => covered.add(b))
    }
  }
  for (const slice of slices) {
    if (buckets.has(slice.bucket) && !covered.has(slice.bucket)) keys.push(slice.bucket)
  }
  return parseSelection(keys)
}

/**
 * Bir anahtara tıklandığında oluşan **yeni** seçim (çoklu seçim, 2026-09-16): anahtarın kovalarının
 * **tamamı** zaten seçiliyse çıkarılır, değilse eklenir. Ana duruma tıklamak iki kovasını **birlikte**
 * taşır; kırılımlarından yalnız biri seçiliyken ana duruma tıklamak **tamamlar** (kaldırmaz) — kullanıcı
 * orada eksik olanı eklemek ister, elindekini kaybetmeyi değil.
 *
 * <p>Modifier tuş **yoktur** (kullanıcı kararı 2026-09-16): düz tıklama ekler/çıkarır. Ctrl/Cmd deseni
 * dokunmatikte ve klavyede ayrı bir yol gerektirirdi; bu grafiklerin klavye yolu ise gerçek
 * `<button>`'lardır ve orada "modifier'lı tıklama" diye bir şey yoktur.
 *
 * <p>Dilimler henüz yüklenmemişse seçim **değişmez**: boş bir hedef, var olan seçimi sessizce silmemeli.
 */
export function toggleSelection(
  selection: ActionSelectionKeys,
  key: string,
  slices: readonly StatusSlice[],
): ActionSelection[] {
  const target = [...expandSelection([key], slices)]
  if (target.length === 0) return [...selection]
  const next = expandSelection(selection, slices)
  const remove = target.every((b) => next.has(b))
  for (const bucket of target) {
    if (remove) next.delete(bucket)
    else next.add(bucket)
  }
  return collapseSelection(next, slices)
}

/**
 * Seçimde **üzerinde iş kalan** (açık) bir statü var mı? — "Görevleri Önceliklendir" buna bakar
 * (077 §K6/SC-009): biten ya da iptal edilmiş bir işi hatırlatmak alıcıya yanlış sinyaldir. Yeni katalogda
 * üzerinde iş kalan tek ana durum **Aksiyon Bekleniyor**'dur (086).
 *
 * <p><b>Seçimsiz</b> hâl ve **karışık** seçim (açık + kapalı) butonu AÇIK bırakır: sunucu toplu
 * hatırlatmada kümeyi ayrıca **açık** kayıtlarla sınırlar, yani kapalı bir işe bildirim üretilemez.
 * Karışık seçimde butonu kapatmak, kullanıcının yapabildiği geçerli bir işi tek bir ek kova yüzünden
 * engellerdi.
 */
export function hasOpenSelection(selection: ActionSelectionKeys): boolean {
  const open: readonly string[] = ['PENDING', 'PENDING_LATE', 'GROUP_PENDING']
  return selection.length === 0 || selection.some((key) => open.includes(key))
}

/**
 * Yönetici Raporu'nun yedi ölçütü (analiz §1.5). `activeCount` dört açık kovanın toplamıdır.
 *
 * <p><b>077: ekranda GÖSTERİLMEZ</b> (KPI kartları kaldırıldı) ama sözleşmede kalır — sunucu
 * değişmezlerini (aktif = dört açık kova, `criticalOpen ≤ pending`) hesaplamaya ve test etmeye devam
 * ediyor (spec §K2).
 */
export interface ActionKpis {
  activeCount: number
  /**
   * Aksiyon Bekleniyor · termini **geçmemiş**. Adı `PersonLoad.pendingCount`'tan bilinçli olarak
   * ayrıdır: oradaki alan ana durumun tamamını (zamanında + gecikmiş) sayar.
   */
  pendingOnTimeCount: number
  pendingLateCount: number
  completedCount: number
  closedCount: number
  /** Termini kaçırılmış **her** aksiyon (üç ana durumun gecikmiş kırılımlarının toplamı). */
  lateCount: number
  /** Çapraz ölçüt (açık kovaların Kritik alt kümesi) — kovalarla toplanmaz. */
  criticalOpenCount: number
}

/** Durum dağılımı dilimi. `bucket` kararlı anahtardır; renk/sıra eşlemesi buna bağlanır. */
export interface StatusSlice {
  bucket: string
  /**
   * Dilimin bağlı olduğu **ana durum** (079); sütun grafiği kırılımları buna göre gruplar. Hiçbir gruba
   * girmeyen kovada (`CANCELLED`) `null`; ana durum diliminde `bucket` ile aynıdır.
   */
  group: string | null
  statusId: number | null
  /** Dile çözülmüş, **tek başına okunabilen** ad (sunucudan) — "Aksiyon Bekleniyor (Gecikmiş)". */
  statusName: string | null
  /**
   * Aynı dilimin **bağlam içi** kısa adı (086) — "Zamanında" / "Gecikmiş". Sütun grafiği bunu yazar;
   * ana durum adı zaten sütunların üstündeki başlıkta durur. Grup dilimlerinde `statusName` ile aynıdır.
   */
  shortName: string | null
  count: number
  /** Yüzde, **bir ondalık** (ör. 28.8) — mockup "53 (%28,8)". */
  percent: number
}

/** `Σ slices[].count === total`; adedi 0 olan kova listede yer almaz. */
export interface StatusDistribution {
  total: number
  slices: StatusSlice[]
}

/**
 * Görevlerin **kaynağa göre** dağılımı (120): doğrudan görev (toplantısız) / toplantı bağımlı görev.
 * Statü halkasıyla aynı küme — `total` halkanın merkezindeki sayıdır. Yüzdeler bir ondalık; ikincisi
 * sunucuda `100 - directPercent` olarak hesaplanır (toplam daima %100).
 */
export interface TaskOriginSplit {
  total: number
  direct: number
  directPercent: number
  meetingBound: number
  meetingBoundPercent: number
}

/**
 * Kişi bazlı satır (analiz §1.7; 086 ile yeni katalog). Değişmez: `activeCount === pendingCount`
 * (açık olan tek ana durum Aksiyon Bekleniyor'dur).
 */
export interface PersonLoad {
  employeeId: string
  fullName: string
  jobTitle: string | null
  activeCount: number
  /** **Aksiyon Bekleniyor** ana durumu: `PENDING + PENDING_LATE`. */
  pendingCount: number
  /** **Tamamlandı** ana durumu: `COMPLETED_ON_TIME + COMPLETED_LATE`. */
  completedCount: number
  /** **Kapatıldı** ana durumu: `CLOSED_ON_TIME + CLOSED_LATE`. */
  closedCount: number
  /** Termini kaçırılmış aksiyonlar — üç ana durumun tamamından toplanır (çubukta ayrı segment değil). */
  lateCount: number
}

/**
 * "En Fazla Gecikme Yaşayan İlk 3 Yönetici" satırı (086). `percent`in paydası **panonun toplam aksiyon
 * sayısıdır** (yöneticinin kendi toplamı değil — kullanıcı kararı 2026-08-13), bu yüzden üç satır aynı
 * ölçeğe oturur.
 */
export interface ManagerDelay {
  employeeId: string
  fullName: string
  jobTitle: string | null
  lateCount: number
  /** Yüzde, bir ondalık (ör. 24.3). */
  percent: number
}

/**
 * "Yönetici Bazlı Ortalama Aksiyon Süresi" satırı (086): statüsü Tamamlandı olan aksiyonlar için
 * `(Tamamlandı'ya çekilme tarihi − yöneticiye atanma tarihi)` ortalaması.
 *
 * `completedCount`, ortalamanın **paydasıdır**: süresi ölçülemeyen (işlem geçmişinde tamamlanma izi
 * bulunmayan) eski kayıtlar hesaba girmediği için `PersonLoad.completedCount`'tan küçük olabilir.
 */
export interface ManagerDuration {
  employeeId: string
  fullName: string
  jobTitle: string | null
  /** Ortalama süre (gün), bir ondalık (ör. 18.4). */
  averageDays: number
  completedCount: number
}

/** Kritik/geciken tablo satırı. Boş değerler `null`/boş dizi → hücre BOŞ bırakılır. */
export interface CriticalActionRow {
  id: number
  recordNumber: string
  name: string
  /** "YYYY-MM-DD"; terminsiz aksiyonda null. */
  deadline: string | null
  importanceLevelId: number | null
  importanceName: string | null
  /** Gecikme (gün); gecikmemişse null. */
  overdueDays: number | null
  assigneeNames: string[]
}

/** Yönetici Raporu'nun tüm verisi — tek uçtan tek yanıt. */
export interface ActionDashboard {
  /** ISO-8601 instant (UTC) — "Son Güncelleme"; sunucu üretir. */
  generatedAt: string
  range: DashboardRange
  /** Gerçekten uygulanan sicil kümesi (kapsamla kesişim sonrası). */
  appliedEmployeeIds: string[]
  scope: DashboardScope
  kpis: ActionKpis
  /** **Donut'un üç dilimi** (086): Aksiyon Bekleniyor / Tamamlandı / Kapatıldı. */
  statusGroups: StatusDistribution
  /** Donut'un **altındaki sütun grafiğini** besleyen altı alt kırılım; yüzdeler aynı toplam üzerinden. */
  statusDistribution: StatusDistribution
  /** Statülerin altındaki doğrudan / toplantı bağımlı görev yüzdeleri (120); eski yanıtta yok. */
  taskOrigin?: TaskOriginSplit
  personLoad: PersonLoad[]
  /** En çok üç satır; hiç geciken aksiyon yoksa **boş**. */
  topDelayedManagers: ManagerDelay[]
  /** Ortalama ↓ sırada; ölçülebilen Tamamlandı kaydı olmayan kişi listede yer almaz. */
  managerDurations: ManagerDuration[]
  criticalActions: CriticalActionRow[]
}

// ── Alt tablo: kova filtreli, sayfalı liste (077 §K4) ──────────────────────────────────────────

/** Satırdaki atanan; baş harf rozeti sicile (`employeeId`) bağlanır, ada değil. */
export interface DashboardActionAssignee {
  employeeId: string
  fullName: string
}

/**
 * Alt tablonun bir satırı. `CriticalActionRow`'dan farkı: "Aksiyon No" ve **önem alanları yoktur**
 * (kolonlar mockup'ta kaldırıldı; önem 079'da düştü) ve atananlar ad dizisi değil rozet üretilebilir
 * kayıtlardır.
 */
export interface DashboardActionRow {
  id: number
  name: string
  /** "YYYY-MM-DD"; terminsiz aksiyonda null. */
  deadline: string | null
  /**
   * Gecikme (gün). Kaynağı satırın durumuna göre değişir (sunucuda türetilir — istemci hesaplamaz):
   * **açık** kayıtta `bugün − termin` (her gün büyür), **sonlanmış** (Tamamlandı/Kapatıldı) kayıtta
   * `bitiş − termin` (sabit: "kaç gün gecikmeyle bitti").
   *
   * Gecikmemişse `null` → hücre BOŞ bırakılır (yer tutucu yazılmaz). `bucket` gecikmiş bir kovaysa bu
   * alan **daima dolu** gelir; 2026-08-22'ye kadar sonlanmış kayıtlarda `null` dönüyordu ve "Gecikmiş"
   * kovası seçilince kolon tamamen boş kalıyordu (086 §K2/C11).
   */
  overdueDays: number | null
  bucket: ActionBucket
  /**
   * Kovanın **ana durumu** (`GROUP_*`); grubu olmayan kovada (İptal) `null`. Eşleme **sunucudan** gelir —
   * istemci kova → grup sözlüğü tutmaz (§K8, donut dilimlerindeki `group` alanıyla aynı desen).
   */
  group: ActionBucketGroup | null
  /**
   * Satırın **görünen durumu** — "Durum" kolonu bunu yazar (2026-09-11). Ana durumun adıdır ("Aksiyon
   * Bekleniyor"/"Tamamlandı"/"Kapatıldı"), kovanın değil: zamanında/gecikmiş ayrımı "Gecikme Süresi"
   * kolonunun bilgisidir. Sunucu çözer (mesaj paketi) → istemcide ikinci bir etiket eşlemesi yok.
   */
  statusName: string | null
  assignees: DashboardActionAssignee[]
}

/** Spring `PagedModel` yanıtı (sunucu-taraflı sayfalama). */
export interface DashboardActionsPage {
  content: DashboardActionRow[]
  page: { size: number; number: number; totalElements: number; totalPages: number }
}

/**
 * Hatırlatma sonucu (077 §K6). `totalCount > actionCount` → toplu çağrı üst sınıra takıldı ve istemci
 * bunu **uyarı** tonunda söyler; `notificationCount === 0` → hiç satır üretilmedi (şablon/alıcı yok).
 *
 * `deferredUntil` doluysa buton **sessiz saat** içinde tıklanmıştır: satırlar yazıldı ama e-posta/mobil
 * bildirim gönderimi bu ana ötelendi → kullanıcıya "gönderildi" değil "sonra iletilecek" uyarısı
 * gösterilir. Pencere 22:00'de başlar, **bitişi sistem parametresinden** gelir; pencere ve öteleme anı
 * **sunucuda** hesaplanır, istemci tarayıcı saatinden kural türetmez ve hedef saati varsaymaz (kuyruğa
 * yazılanla ekranda söylenen ayrışmasın).
 */
export interface ReminderResult {
  actionCount: number
  notificationCount: number
  totalCount: number
  deferredUntil: string | null
}

/**
 * "Görevleri Önceliklendir" penceresinin **kalan süre** seçenekleri (talep revizyonu 2026-10-21).
 * `D<N>` → termine **0..N** gün kalanlar; `D30_PLUS` → **30 günden fazla** (31+) kalanlar. Anahtarlar
 * sunucunun `ReminderCriteria.Window` adlarıdır ve sunucu yalnız bunları kabul eder — liste değişirse
 * ikisi birlikte değişir.
 */
export const PRIORITY_REMAINING = [
  'D1',
  'D2',
  'D3',
  'D4',
  'D5',
  'D10',
  'D20',
  'D30',
  'D30_PLUS',
] as const

export type PriorityRemaining = (typeof PRIORITY_REMAINING)[number]

/**
 * Seçeneğin gün sınırı — etiket ("3 Gün" / "30 Günden Fazla") ve pencerenin özet cümlesi bundan
 * üretilir; ikinci bir gün tablosu tutulmaz.
 *
 * @returns `over` → sınırın **üstü** (30 günden fazla); değilse 0..`days`
 */
export function remainingBound(key: PriorityRemaining): { days: number; over: boolean } {
  const over = key.endsWith('_PLUS')
  return { days: Number(key.slice(1).replace('_PLUS', '')), over }
}

/**
 * Toplu önceliklendirmenin hedefi. İki ölçüt **birleşir** (VEYA): "5 gün + Geciken Görevler" = termine
 * 0–5 gün kalanlar ∪ bütün gecikenler. Kalan süre gecikenleri **kapsamaz**. En az biri dolu olmalıdır —
 * ikisi de boşsa sunucu `400` döner (kümenin tamamına gönderim yolu kapalı).
 *
 * @property remaining kalan süre penceresi; `null` → bu ölçüt yok
 * @property overdue hedef teslim tarihi geçmiş açık görevler de dahil mi
 */
export interface PriorityCriteria {
  remaining: PriorityRemaining | null
  overdue: boolean
}

/** Kişi seçici seçeneği = kapsam kişisi. */
export interface DashboardManager {
  employeeId: string
  fullName: string
  jobTitle: string | null
}

// ── Raporum (analiz §2) ────────────────────────────────────────────────────────────────────────

/**
 * Delegasyon özeti. **`total` dilimlerin toplamıdır**, taban kümenin boyu değil: 2026-08-21'den beri
 * sorumlusu hiç atanmamış "yalnız oluşturduklarım" hiçbir dilime girmiyor (spec §K8) → iki dilim taban
 * kümeyi artık **tam bölmez**. Toplamı dilimlerden kurmak yüzdelerin %100 etmesini korur.
 */
export interface DelegationSummary {
  total: number
  assignedToMeCount: number
  assignedToMePercent: number
  delegatedCount: number
  delegatedPercent: number
}

/** Kalan süre aralığı; `bucket` kararlı anahtar, etiket istemcide (i18n `home.my.remaining.<bucket>`). */
export interface RemainingTimeBucket {
  bucket: 'D0_7' | 'D8_30' | 'D31_90' | 'D91_PLUS'
  count: number
  percent: number
}

/**
 * Raporum'un "Geciken Aksiyonlar" tablosunun satırı. `remainingDays` 2026-08-19'da kaldırıldı: tek
 * tüketicisi olan "Yaklaşan Kritik Aksiyon" bölümü analizin yeni bölüm listesinde yok.
 */
export interface MyActionRow {
  id: number
  recordNumber: string
  name: string
  deadline: string | null
  overdueDays: number | null
}

/** "En Fazla Aksiyon Atadığım Yöneticiler" satırı. `assignedCount = completed + ongoing`. */
export interface DelegateePerformance {
  employeeId: string
  fullName: string | null
  jobTitle: string | null
  completedCount: number
  ongoingCount: number
  assignedCount: number
  /** Yüzde, bir ondalık (ör. 71.4). */
  completionRate: number
}

/**
 * Raporum'un **özet** bölümleri (2026-08-19 revizyonu). Yedinci bölüm ("Aksiyon Beklenen Maddeler")
 * burada **yoktur**: sayfalı olduğu için ayrı uçtan gelir ({@link MyActionsPage}) — sayfa/sıralama
 * değişimi donut'ları yeniden çekmemelidir.
 *
 * <p>`statusDistribution` ve `delegatedStatus` artık **üç ana durumu** taşır (GROUP_*); altı kova kırılımı
 * ve kalkmış İptal dilimi Raporum'dan da düştü.
 */
export interface MyReport {
  generatedAt: string
  range: DashboardRange
  statusDistribution: StatusDistribution
  /** §1'in altındaki doğrudan / toplantı bağımlı görev yüzdeleri (120); eski yanıtta yok. */
  taskOrigin?: TaskOriginSplit
  delegation: DelegationSummary
  delegatedStatus: StatusDistribution
  remainingTime: RemainingTimeBucket[]
  overdueActions: MyActionRow[]
  topDelegatees: DelegateePerformance[]
}

// ── Raporum: "Aksiyon Beklenen Maddeler" tablosu (Raporum revizyonu §7) ────────────────────────

/** Delegasyon Özeti'nin iki yarısı — tablonun kapsam boyutu. */
export const MY_ACTION_SCOPES = ['MINE', 'DELEGATED'] as const

export type MyActionScope = (typeof MY_ACTION_SCOPES)[number]

/** Kalan süre aralıkları — grafikle tablonun **aynı** anahtar kümesini kullanması için tek kaynak. */
export const REMAINING_BUCKETS = ['D0_7', 'D8_30', 'D31_90', 'D91_PLUS'] as const

export type RemainingBucketKey = (typeof REMAINING_BUCKETS)[number]

/** Sunucuya "statü süzgeci uygulama" demenin yolu (Delegasyon Özeti'nin dilimleri statü değildir). */
export const ALL_STATUSES = 'ALL'

/**
 * Tablonun süzgeci — panonun **dört** grafiği de buraya yazar (analiz §1.3/§2.3/§3.3/§4.4).
 *
 * @property scope §2'nin dilimi: bana atanan / devrettiklerim
 * @property bucket §1 ve §3 donut'larının dilimi (ana durum), ya da {@link ALL_STATUSES}
 * @property remaining §4'ün barı; `null` → aralık süzgeci yok
 */
export interface MyActionSelection {
  scope: MyActionScope
  bucket: string
  remaining: RemainingBucketKey | null
}

/**
 * Süzgeçsiz (varsayılan) hâl: **bana atanmış, Aksiyon Bekleniyor** — bölümün adının söylediği küme
 * (analiz §7.1). Sunucu da aynı varsayılanı uygular; buradaki değer istemcinin seçili dilimi doğru
 * işaretleyebilmesi içindir.
 */
export const DEFAULT_MY_SELECTION: MyActionSelection = {
  scope: 'MINE',
  bucket: 'GROUP_PENDING',
  remaining: null,
}

/** Sayfalı tablo satırı — Yönetici Raporu'nun alt tablosuyla **aynı** şekil (Reuse Mandate). */
export type MyActionsPage = DashboardActionsPage

/**
 * URL'den süzgeci çözer (`?myScope=&myBucket=&myRemaining=`). Tanınmayan değer varsayılana düşer →
 * bozuk bir yer imi tabloyu boş bırakmaz (sunucu da aynı kuralı uygular).
 *
 * <p>Parametre adları Yönetici Raporu'nun `bucket`'ından **ayrıdır**: iki pano aynı URL'i paylaşıyor ve
 * panolar arası geçişte diğerinin seçimi buraya sızmamalı.
 */
export function parseMySelection(params: URLSearchParams): MyActionSelection {
  const scope = params.get('myScope')
  const bucket = params.get('myBucket')
  const remaining = params.get('myRemaining')
  const knownBuckets: readonly string[] = [...ACTION_BUCKETS, ...ACTION_BUCKET_GROUPS, ALL_STATUSES]
  const knownRemaining: readonly string[] = REMAINING_BUCKETS
  return {
    scope: scope === 'DELEGATED' ? 'DELEGATED' : 'MINE',
    bucket:
      bucket != null && knownBuckets.includes(bucket) ? bucket : DEFAULT_MY_SELECTION.bucket,
    remaining:
      remaining != null && knownRemaining.includes(remaining)
        ? (remaining as RemainingBucketKey)
        : null,
  }
}

/**
 * Süzgeci URL'e yazar; **varsayılan değerler yazılmaz** → süzgeçsiz pano temiz bir adreste durur ve
 * "seçimi kaldır" gerçekten kaldırılmış görünür.
 */
export function writeMySelection(
  params: URLSearchParams,
  selection: MyActionSelection,
): URLSearchParams {
  const next = new URLSearchParams(params)
  const set = (key: string, value: string | null, fallback: string | null) => {
    if (value == null || value === fallback) {
      next.delete(key)
    } else {
      next.set(key, value)
    }
  }
  set('myScope', selection.scope, DEFAULT_MY_SELECTION.scope)
  set('myBucket', selection.bucket, DEFAULT_MY_SELECTION.bucket)
  set('myRemaining', selection.remaining, null)
  return next
}

/** İki süzgeç aynı mı? — dilime yeniden tıklamanın "seçimi kaldır" olması buna bakar. */
export function isSameMySelection(a: MyActionSelection, b: MyActionSelection): boolean {
  return a.scope === b.scope && a.bucket === b.bucket && a.remaining === b.remaining
}

// ── Sorgu durumu ───────────────────────────────────────────────────────────────────────────────

/** Pano sorgu parametreleri; `employeeIds` boş → kapsamın tamamı. */
export interface DashboardQuery {
  employeeIds: string[]
  period: DashboardPeriod
  /** Yalnız `period === 'CUSTOM'` iken anlamlı; "YYYY-MM-DD". */
  startDate: string | null
  endDate: string | null
}

export const EMPTY_QUERY: DashboardQuery = {
  employeeIds: [],
  period: DEFAULT_PERIOD,
  startDate: null,
  endDate: null,
}

/** Raporum'un başlangıç sorgusu: kişi filtresi yok, dönem **Son 1 Ay** (analiz varsayılanı). */
export const EMPTY_MY_QUERY: DashboardQuery = {
  employeeIds: [],
  period: DEFAULT_MY_PERIOD,
  startDate: null,
  endDate: null,
}

/**
 * Filtreler varsayılanda mı? ("Filtreleri Temizle" yalnız değilse görünür — analiz §1.4.2)
 *
 * @param defaultPeriod panonun kendi varsayılanı; iki pano 2026-08-19'dan beri farklı varsayılan
 *   kullanıyor (Yönetici Raporu Son 1 Yıl, Raporum Son 1 Ay) → sabit bir değere bakmak Raporum'da
 *   "Filtreleri Temizle"yi hiç kaybolmayan bir kontrol yapardı.
 */
export function isDefaultQuery(
  query: DashboardQuery,
  defaultPeriod: DashboardPeriod = DEFAULT_PERIOD,
): boolean {
  return (
    query.employeeIds.length === 0 &&
    query.period === defaultPeriod &&
    query.startDate == null &&
    query.endDate == null
  )
}

/**
 * Sorgu sunucuya gönderilebilir durumda mı? Özel aralıkta iki tarih de dolmadan istek atılmaz —
 * aksi hâlde kullanıcı ilk tarihi seçer seçmez sunucudan 400 dönerdi (analiz §1.4.1.2).
 */
export function isQueryReady(query: DashboardQuery): boolean {
  if (query.period !== 'CUSTOM') return true
  return query.startDate != null && query.endDate != null
}

/**
 * Özel tarih aralığının istemci tarafı doğrulaması (analiz §1.4.1.2). Geçerliyse `null`, değilse
 * gösterilecek i18n anahtarı döner. Sunucu **aynı** kuralları ayrıca uygular (istemciye güvenilmez).
 */
export function customRangeError(query: DashboardQuery, today: string): string | null {
  if (query.period !== 'CUSTOM') return null
  if (query.startDate == null || query.endDate == null) return null // henüz eksik — hata değil
  if (query.startDate > query.endDate) return 'home.filters.errorStartAfterEnd'
  if (query.endDate > today) return 'home.filters.errorEndInFuture'
  return null
}
